import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { useDispatch } from "react-redux";
import Moment from "moment";
import A_Button from "../../../../Atoms/Inputs/Buttons/A_Button";
import A_SimpleDialog from "../../../../Atoms/Feedback/Dialogs/A_SimpleDialog";
import A_DatePicker from "../../../../Atoms/Inputs/DateTimePickers/A_DatePicker";
import useStyles from "./AddPricingPerimeterForm.styles";
import { httpPost } from "../../../../../../Utils/apis/apis";
import * as toastActions from "../../../../../../store/actions/toastActions";
import { toastSeverity } from "../../../../../../Utils/config/toastConfig";
import A_SecuredAction from "../../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../../Utils/Helpers/pageSecurity";

// Tax Rate is prefilled at the standard 25% - the user can overwrite it.
const DEFAULT_TAX_RATE = "25";

const INITIAL_STATE = {
  costOfFundsRate: "",
  opexRate: "",
  taxRate: DEFAULT_TAX_RATE,
  effectiveDate: null,
};

const toEffectiveDateTime = (epochSeconds) => {
  const now = Moment();
  return Moment.unix(epochSeconds)
    .set({ hour: now.hour(), minute: now.minute(), second: now.second() })
    .format("YYYY-MM-DD HH:mm:ss");
};

const AddPricingPerimeterForm = (props) => {
  const { open, onClose, title, afterClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  // A_DatePicker emits epoch seconds, or 0 when cleared/invalid.
  const handleDateChange = (epochSeconds) => {
    setFormData((prev) => ({ ...prev, effectiveDate: epochSeconds || null }));
    if (errors.effectiveDate) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.effectiveDate;
        return next;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.costOfFundsRate && formData.costOfFundsRate !== 0) {
      newErrors.costOfFundsRate = "Cost of Funds Rate is required";
    } else if (
      isNaN(formData.costOfFundsRate) ||
      Number(formData.costOfFundsRate) < 0
    ) {
      newErrors.costOfFundsRate = "Must be a valid percentage";
    }

    if (!formData.opexRate && formData.opexRate !== 0) {
      newErrors.opexRate = "Opex Rate is required";
    } else if (isNaN(formData.opexRate) || Number(formData.opexRate) < 0) {
      newErrors.opexRate = "Must be a valid percentage";
    }

    if (!formData.taxRate && formData.taxRate !== 0) {
      newErrors.taxRate = "Tax Rate is required";
    } else if (isNaN(formData.taxRate) || Number(formData.taxRate) < 0) {
      newErrors.taxRate = "Must be a valid percentage";
    }

    if (!formData.effectiveDate) {
      newErrors.effectiveDate = "Effective Date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const pricingPerimeterData = {
        CostOfFundsRate: parseFloat(formData.costOfFundsRate) || 0,
        OpexRate: parseFloat(formData.opexRate) || 0,
        TaxRate: parseFloat(formData.taxRate) || 0,
        EffectiveDate: toEffectiveDateTime(formData.effectiveDate),
      };

      const payload = [
        {
          id: "savePricingPerimeter",
          spName: "DF.usp_AddROEInputs",
          parameters: {
            JSON: JSON.stringify(pricingPerimeterData),
          },
        },
      ];

      const response = await httpPost(
        "/SQLQuery/getDataFromMultipleSPs?chartSPs=false",
        payload
      );
      if (response instanceof Error) {
        throw response;
      }

      if (response && !response.Message && !response.error) {
        dispatch(
          toastActions.setToast({
            type: toastSeverity.SUCCESS,
            severity: toastSeverity.SUCCESS,
            message: "Pricing Perimeter added successfully",
            duration: 3000,
          })
        );
        resetForm();
        onClose();
        if (afterClose) {
          setTimeout(() => {
            const pathSegments = window.location.pathname
              .split("/")
              .filter(Boolean);
            const pageUrl =
              pathSegments.length > 0
                ? pathSegments[0]
                : window.location.pathname;
            afterClose({
              pageUrl: pageUrl
            });
          }, 300);
        }
        return;
      }

      const errorMessage =
        response?.Message ||
        response?.error ||
        "Failed to add Pricing Perimeter. Please try again.";
      throw new Error(errorMessage);
    } catch (error) {
      console.error("Error saving pricing perimeter:", error);
      const errorMessage =
        error.response?.data?.Message ||
        error.message ||
        "Failed to add Pricing Perimeter. Please try again.";
      dispatch(
        toastActions.setToast({
          type: toastSeverity.ERROR,
          severity: toastSeverity.ERROR,
          message: errorMessage,
        })
      );
      setErrors((prev) => ({
        ...prev,
        submit: errorMessage,
      }));
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_STATE);
    setErrors({});
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const renderRateField = (name, label) => (
    <TextField
      fullWidth
      label={label}
      name={name}
      type="number"
      value={formData[name]}
      onChange={handleChange}
      variant="outlined"
      size="small"
      className={classes.formField}
      inputProps={{ step: "1", min: "0" }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end" className={classes.adornment}>
            %
          </InputAdornment>
        ),
      }}
      placeholder="0.00"
      error={Boolean(errors[name])}
      helperText={errors[name]}
      disabled={saving}
    />
  );

  const dialogContent = (
    <Box className={classes.formContainer}>
      <Box className={classes.sectionCard}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderRateField("costOfFundsRate", "Cost of Funds Rate")}
          </Grid>
          <Grid item xs={12}>
            {renderRateField("opexRate", "Opex Rate")}
          </Grid>
          <Grid item xs={12}>
            {renderRateField("taxRate", "Tax Rate")}
          </Grid>
          <Grid item xs={12}>
            <A_DatePicker
              fullWidth
              label="Effective Date"
              inputVariant="outlined"
              margin="none"
              size="small"
              className={classes.dateField}
              defaultValue={formData.effectiveDate}
              onChange={handleDateChange}
              disabled={saving}
              error={Boolean(errors.effectiveDate)}
              helperText={errors.effectiveDate}
            />
          </Grid>
        </Grid>
      </Box>

      {errors.submit && (
        <Typography className={classes.errorText}>{errors.submit}</Typography>
      )}
    </Box>
  );

  const dialogActions = (
    <Box className={classes.dialogFooter}>
      <A_Button
        label="Cancel"
        onClick={handleCancel}
        premiumVariant="secondary"
        className={classes.cancelBtn}
        disabled={saving}
        size="small"
      />
      <A_SecuredAction permission={pageSecurityActions.UPDATE}>
        <A_Button
          label={saving ? "Saving..." : "Save"}
          onClick={handleSubmit}
          premiumVariant="primary"
          className={classes.submitBtn}
          disabled={saving}
          size="small"
        />
      </A_SecuredAction>
    </Box>
  );

  return (
    <A_SimpleDialog
      open={open}
      title={title || "Add Pricing Perimeter"}
      dialogContent={dialogContent}
      dialogActions={dialogActions}
      onClose={handleCancel}
      fullWidth
      maxWidth="sm"
      showclosebutton="true"
      disableBackdropClick={true}
      liquidGlass={false}
    />
  );
};

AddPricingPerimeterForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  afterClose: PropTypes.func,
};

export default AddPricingPerimeterForm;
